import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
  useMediaQuery,
} from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { sendInvitation } from "../customerPartnershipsSlice";
import { CustomerPartnershipsState, InvitationStatusCode } from "../models/models";
import customerCompaniesApi from "../../companies/api/customerCompaniesApi";
import { CustomerCompany } from "../../companies/models/models";
import { dt, accentGradientLight, accentGradientLightHover, accentShimmer } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";

/* -- Blue accent tokens (invite/create action) -- */
const blue = dt.blue;
const blueDark = dt.blueHover;
const blueSoft = dt.blueSoft;

interface InvitePartnerDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (email?: string, companyName?: string) => void;
  prefillEmail?: string;
  prefillCompanyName?: string;
  prefillCustomerType?: number;
  /** The viewer's (inviter's) company type: 1=MachineOperator, 2=ServiceProvider, 3=Both */
  viewerCustomerType?: number;
  mode?: "full" | "profile";
}

/**
 * Keep a ref to onSuccess so the async handleSubmit closure always calls
 * the latest version of the callback, even after React re-renders caused
 * by Redux state changes (actionLoading toggling) between the await
 * and the callback invocation.
 */
function useLatestRef<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

/* -- Shared MUI Select sx for dropdowns -- */
const dropdownSelectSx = {
  flex: 1,
  fontFamily: dt.font,
  fontSize: "14px",
  fontWeight: 500,
  backgroundColor: dt.inputBg,
  borderRadius: dt.radiusSm,
  height: "44px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: dt.cardBorder,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: dt.blue,
    borderWidth: "1.5px",
  },
  "& .MuiSelect-select": {
    fontFamily: dt.font,
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
  },
};

const dropdownMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 200,
      borderRadius: dt.radiusSm,
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    },
  },
};

const dropdownMenuItemSx = {
  fontFamily: dt.font,
  fontSize: "14px",
  py: 0.75,
};

const InvitePartnerDialog: React.FC<InvitePartnerDialogProps> = ({
  open,
  onClose,
  onSuccess,
  prefillEmail,
  prefillCompanyName,
  prefillCustomerType,
  viewerCustomerType,
  mode = "full",
}) => {
  useRedesignSetup();
  const { translate } = useTranslation(["partnerships", "common"]);
  const dispatch = useAppDispatch();
  const isPhone = useMediaQuery("(max-width:600px)");
  const { actionLoading } = useAppSelector<CustomerPartnershipsState>(
    (state: any) => state.customerPartnerships
  );

  // Stable refs to avoid stale-closure issues in async handleSubmit
  const onSuccessRef = useLatestRef(onSuccess);
  const onCloseRef = useLatestRef(onClose);

  const isProfileMode = mode === "profile";

  // Determine if viewer is non-hybrid: auto-set opposite company type
  // MachineOperator(1) → opposite is ServiceProvider(2)
  // ServiceProvider(2) → opposite is MachineOperator(1)
  // Both(3) or undefined → show dropdown (user chooses)
  const isViewerNonHybrid = viewerCustomerType === 1 || viewerCustomerType === 2;
  const autoOppositeType = viewerCustomerType === 1 ? "2" : viewerCustomerType === 2 ? "1" : "";

  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [companyType, setCompanyType] = useState<string>("");
  const [emailError, setEmailError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [sameTypeWarning, setSameTypeWarning] = useState(false);

  // Company name autocomplete state (only used in full mode)
  const [companyName, setCompanyName] = useState("");
  const [companySuggestions, setCompanySuggestions] = useState<CustomerCompany[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const companyFieldRef = useRef<HTMLDivElement>(null);
  const suggestionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Inject dialog-specific keyframes
  useEffect(() => {
    const styleId = "invite-partner-dialog-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes dialogSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes invitePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(52,152,219,0.15); }
          50% { box-shadow: 0 0 0 8px rgba(52,152,219,0); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Pre-fill from props when dialog opens
  useEffect(() => {
    if (open) {
      if (prefillEmail) {
        setEmail(prefillEmail);
      }
      if (prefillCompanyName) {
        setCompanyName(prefillCompanyName);
      }
      // For non-hybrid viewers, always set the opposite type regardless of prefill
      if (isViewerNonHybrid) {
        setCompanyType(autoOppositeType);
      } else if (prefillCustomerType) {
        setCompanyType(String(prefillCustomerType));
      }
      setSameTypeWarning(false);
    }
  }, [open, prefillEmail, prefillCompanyName, prefillCustomerType, isViewerNonHybrid, autoOppositeType]);

  const searchCompanies = useCallback(async (text: string) => {
    if (text.trim().length < 2) {
      setCompanySuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setSuggestionsLoading(true);
    try {
      const result = await customerCompaniesApi.getCompanies({
        search: text.trim(),
        pageNo: 1,
        pageSize: 5,
      });
      setCompanySuggestions(result.items || []);
      setHighlightedIndex(-1);
      setShowSuggestions(true);
    } catch {
      setCompanySuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  const handleCompanyNameChange = (value: string) => {
    setCompanyName(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 2) {
      setCompanySuggestions([]);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      return;
    }
    debounceRef.current = setTimeout(() => {
      searchCompanies(value);
    }, 300);
  };

  const handleSelectCompany = (company: CustomerCompany) => {
    setCompanyName(company.name);
    setShowSuggestions(false);
    setCompanySuggestions([]);
    setHighlightedIndex(-1);

    // Pre-fill email from selected company
    if (company.email) {
      setEmail(company.email);
      setEmailError("");
    }

    // Check for same-type warning: if viewer is non-hybrid and selected company
    // is the same type (e.g., both are MachineOperator), warn the user
    if (isViewerNonHybrid && company.customerType && company.customerType === viewerCustomerType) {
      setSameTypeWarning(true);
    } else {
      setSameTypeWarning(false);
    }

    // For non-hybrid viewers, always use the auto-opposite type
    if (isViewerNonHybrid) {
      setCompanyType(autoOppositeType);
    } else if (company.customerType) {
      setCompanyType(String(company.customerType));
    }
  };

  // Keyboard navigation for suggestions dropdown
  const handleCompanyKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || companySuggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < companySuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : companySuggestions.length - 1
        );
        break;
      case "Enter":
        if (highlightedIndex >= 0 && highlightedIndex < companySuggestions.length) {
          e.preventDefault();
          handleSelectCompany(companySuggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Close suggestions dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (companyFieldRef.current && !companyFieldRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll highlighted suggestion into view
  useEffect(() => {
    if (highlightedIndex >= 0 && suggestionRefs.current[highlightedIndex]) {
      suggestionRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  const validateEmail = (value: string): boolean => {
    if (!value.trim()) {
      setEmailError(translate("partnerships:validation.emailRequired"));
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim())) {
      setEmailError(translate("partnerships:validation.emailInvalid"));
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validateEmail(email)) return;
    if (message.length > 1000) return;

    setSubmitError("");

    try {
      const result = await dispatch(
        sendInvitation({
          inviteeEmail: email.trim(),
          message: message.trim() || undefined,
          expectedCustomerType: companyType ? parseInt(companyType, 10) : undefined,
        })
      ).unwrap();

      const sentEmail = email.trim();

      // Use refs instead of closure-captured callbacks.  After the await
      // above, React may have re-rendered the parent tree (e.g. Redux
      // actionLoading toggle), giving InvitePartnerButton a new
      // handleSuccess / onSuccess function.  The closure still holds the
      // OLD onSuccess from the render when the user clicked "Send".
      // If any layer in the callback chain (Button → Page) recreated its
      // callback without useCallback, the old reference may point to a
      // stale or disconnected function.  Reading from a ref guarantees we
      // always call the LATEST callback the parent provided.
      const latestOnSuccess = onSuccessRef.current;
      const latestOnClose = onCloseRef.current;

      if (result && result.statusCode === InvitationStatusCode.Accepted) {
        const companyName = result.inviteeCustomerName || sentEmail;
        if (latestOnSuccess) latestOnSuccess(sentEmail, companyName);
      } else {
        if (latestOnSuccess) latestOnSuccess(sentEmail);
      }

      setEmail("");
      setMessage("");
      setCompanyType("");
      setSameTypeWarning(false);
      setCompanyName("");
      setCompanySuggestions([]);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      latestOnClose();
    } catch (err: any) {
      setSubmitError(err?.message || translate("partnerships:invite.errorMessage"));
    }
  };

  const handleClose = () => {
    if (!actionLoading) {
      setEmail("");
      setMessage("");
      setCompanyType("");
      setEmailError("");
      setSubmitError("");
      setSameTypeWarning(false);
      setCompanyName("");
      setCompanySuggestions([]);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      onClose();
    }
  };

  // Dialog title: profile mode uses company name, full mode uses generic title
  const dialogTitle = isProfileMode && prefillCompanyName
    ? translate("partnerships:invite.dialogTitleProfile", { companyName: prefillCompanyName })
    : translate("partnerships:invite.dialogTitle");

  /* -- Section label renderer -- */
  const renderSectionLabel = (
    icon: React.ReactNode,
    label: string
  ) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        mb: 1,
      }}
    >
      {icon}
      <Box
        sx={{
          fontSize: "13px",
          fontWeight: 600,
          color: dt.textPrimary,
          textTransform: "uppercase",
          letterSpacing: "0.8px",
        }}
      >
        {label}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radiusLg,
          overflow: "hidden",
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          animation: "dialogSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          ...(isPhone && {
            margin: "16px",
            width: "calc(100% - 32px)",
            maxWidth: "none",
          }),
        },
      }}
    >
      {/* -- Header with blue gradient accent bar -- */}
      <Box
        sx={{
          height: "4px",
          background: accentShimmer,
        }}
      />
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 0.5,
          pt: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: blueSoft,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "invitePulse 2s ease-in-out infinite",
            }}
          >
            <HandshakeOutlinedIcon
              sx={{ color: blue, fontSize: 22 }}
            />
          </Box>
          <Box
            sx={{
              fontWeight: 700,
              fontSize: { xs: "18px", sm: "20px" },
              color: dt.textPrimary,
              letterSpacing: "-0.3px",
            }}
          >
            {dialogTitle}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0.5, pb: 0 }}>
        {/* -- Subtitle -- */}
        <Box
          sx={{
            fontSize: "14px",
            color: dt.textSecondary,
            mb: 3,
            mt: 0.5,
            lineHeight: 1.6,
          }}
        >
          {translate("partnerships:invite.dialogDescription")}
        </Box>

        {submitError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: dt.radiusSm }}>
            {submitError}
          </Alert>
        )}

        {/* -- Company Name Autocomplete (hidden in profile mode) -- */}
        {!isProfileMode && (
          <Box sx={{ mb: 2.5, position: "relative" }} ref={companyFieldRef}>
            {renderSectionLabel(
              <BusinessOutlinedIcon sx={{ fontSize: 15, color: dt.textMuted }} />,
              translate("partnerships:invite.companyNameLabel")
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                background: dt.inputBg,
                borderRadius: dt.radiusSm,
                border: "1.5px solid transparent",
                padding: "0 12px",
                height: "44px",
                transition: "all 0.2s ease",
                "&:focus-within": {
                  border: `1.5px solid ${dt.blue}`,
                  boxShadow: `0 0 0 3px ${dt.blueGlow}`,
                },
              }}
            >
              <input
                type="text"
                value={companyName}
                onChange={(e) => handleCompanyNameChange(e.target.value)}
                onKeyDown={handleCompanyKeyDown}
                onFocus={() => {
                  if (companySuggestions.length > 0) setShowSuggestions(true);
                }}
                placeholder={translate("partnerships:invite.companyNamePlaceholder")}
                disabled={actionLoading}
                style={{
                  border: "none",
                  outline: "none",
                  width: "100%",
                  fontSize: "14px",
                  fontFamily: 'var(--font-sans)',
                  color: dt.textPrimary,
                  background: "transparent",
                }}
              />
              {suggestionsLoading && (
                <CircularProgress size={16} sx={{ color: dt.textMuted, flexShrink: 0 }} />
              )}
            </Box>
            {/* Suggestions dropdown */}
            {showSuggestions && companySuggestions.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  backgroundColor: dt.cardBg,
                  borderRadius: dt.radiusSm,
                  border: `1px solid ${dt.cardBorder}`,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
                  mt: 0.5,
                  maxHeight: "240px",
                  overflowY: "auto",
                }}
              >
                {companySuggestions.map((company, index) => (
                  <Box
                    key={company.id}
                    ref={(el: HTMLDivElement | null) => { suggestionRefs.current[index] = el; }}
                    onClick={() => handleSelectCompany(company)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      padding: "10px 14px",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      backgroundColor:
                        index === highlightedIndex ? dt.blueGlow : "transparent",
                      "&:hover": {
                        backgroundColor: dt.blueGlow,
                      },
                      "&:not(:last-child)": {
                        borderBottom: `1px solid ${dt.rowBorder}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: dt.inputBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <BusinessOutlinedIcon sx={{ fontSize: "14px", color: dt.textMuted }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: dt.textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {company.name}
                      </Box>
                      {(company.address?.city || company.address?.countryName) && (
                        <Box
                          sx={{
                            fontSize: "11px",
                            color: dt.textMuted,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {[company.address.city, company.address.countryName]
                            .filter(Boolean)
                            .join(", ")}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* -- Email Input -- */}
        <Box sx={{ mb: 2.5 }}>
          {renderSectionLabel(
            <EmailOutlinedIcon sx={{ fontSize: 15, color: dt.textMuted }} />,
            translate("partnerships:invite.emailLabel")
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              background: dt.inputBg,
              borderRadius: dt.radiusSm,
              border: `1.5px solid ${emailError ? "#C0392B" : "transparent"}`,
              padding: "0 12px",
              height: "44px",
              transition: "all 0.2s ease",
              "&:focus-within": {
                border: `1.5px solid ${emailError ? "#C0392B" : dt.blue}`,
                boxShadow: `0 0 0 3px ${emailError ? "rgba(192,57,43,0.12)" : dt.blueGlow}`,
              },
            }}
          >
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) validateEmail(e.target.value);
              }}
              onBlur={() => {
                if (email) validateEmail(email);
              }}
              placeholder={translate("partnerships:invite.emailPlaceholder")}
              disabled={actionLoading}
              style={{
                border: "none",
                outline: "none",
                width: "100%",
                fontSize: "14px",
                fontFamily: 'var(--font-sans)',
                color: dt.textPrimary,
                background: "transparent",
              }}
            />
          </Box>
          {emailError && (
            <Box sx={{ fontSize: "12px", color: "#C0392B", mt: 0.5 }}>{emailError}</Box>
          )}
          {/* Helper text when profile mode has no pre-filled email */}
          {isProfileMode && !prefillEmail && !email && !emailError && (
            <Box sx={{ fontSize: "12px", color: dt.textMuted, mt: 0.5, fontStyle: "italic" }}>
              {translate("partnerships:invite.noEmailOnFile")}
            </Box>
          )}
        </Box>

        {/* -- Same-type warning (shown when non-hybrid user selects a same-type company) -- */}
        {sameTypeWarning && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: dt.radiusSm }}>
            {translate("partnerships:invite.sameTypeWarning")}
          </Alert>
        )}

        {/* -- Company Type Dropdown --
             Hidden in profile mode.
             For non-hybrid viewers (type 1 or 2), the type is auto-set to the opposite — no dropdown shown.
             For hybrid viewers (type 3) or unknown type, show the dropdown. -- */}
        {!isProfileMode && !isViewerNonHybrid && (
          <Box sx={{ mb: 2.5 }}>
            {renderSectionLabel(
              <CategoryOutlinedIcon sx={{ fontSize: 15, color: dt.textMuted }} />,
              translate("partnerships:invite.companyTypeLabel")
            )}
            <Select
              value={companyType}
              onChange={(e) => setCompanyType(e.target.value as string)}
              displayEmpty
              disabled={actionLoading}
              size="small"
              fullWidth
              MenuProps={dropdownMenuProps}
              sx={{
                ...dropdownSelectSx,
                color: companyType ? dt.textPrimary : dt.textMuted,
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Box sx={{ color: dt.textMuted, fontFamily: dt.font, fontSize: "14px" }}>
                      {translate("partnerships:invite.companyTypePlaceholder")}
                    </Box>
                  );
                }
                const labels: Record<string, string> = {
                  "2": translate("partnerships:invite.companyTypeServiceProvider"),
                  "1": translate("partnerships:invite.companyTypeMachineOperator"),
                  "3": translate("partnerships:invite.companyTypeBoth"),
                };
                return labels[selected] || selected;
              }}
            >
              <MenuItem value="2" sx={dropdownMenuItemSx}>
                {translate("partnerships:invite.companyTypeServiceProvider")}
              </MenuItem>
              <MenuItem value="1" sx={dropdownMenuItemSx}>
                {translate("partnerships:invite.companyTypeMachineOperator")}
              </MenuItem>
              <MenuItem value="3" sx={dropdownMenuItemSx}>
                {translate("partnerships:invite.companyTypeBoth")}
              </MenuItem>
            </Select>
          </Box>
        )}

        {/* -- Auto-detect language info -- */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2.5,
            padding: "10px 14px",
            borderRadius: dt.radiusSm,
            background: "rgba(52, 152, 219, 0.04)",
            border: `1px solid rgba(52, 152, 219, 0.12)`,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: "16px", color: dt.blue, flexShrink: 0 }} />
          <Box sx={{ fontSize: "12px", color: dt.textSecondary, lineHeight: 1.5 }}>
            {translate("partnerships:invite.languageAutoDetected")}
          </Box>
        </Box>

        {/* -- Message Textarea -- */}
        <Box sx={{ mb: 1 }}>
          {renderSectionLabel(
            <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 15, color: dt.textMuted }} />,
            translate("partnerships:invite.messageLabel")
          )}
          <Box
            sx={{
              background: dt.inputBg,
              borderRadius: dt.radiusSm,
              border: `1.5px solid ${message.length > 1000 ? "#C0392B" : "transparent"}`,
              padding: "12px",
              transition: "all 0.2s ease",
              "&:focus-within": {
                border: `1.5px solid ${message.length > 1000 ? "#C0392B" : dt.blue}`,
                boxShadow: `0 0 0 3px ${message.length > 1000 ? "rgba(192,57,43,0.12)" : dt.blueGlow}`,
              },
            }}
          >
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={translate("partnerships:invite.messagePlaceholder")}
              disabled={actionLoading}
              rows={4}
              style={{
                border: "none",
                outline: "none",
                width: "100%",
                fontSize: "14px",
                fontFamily: 'var(--font-sans)',
                color: dt.textPrimary,
                background: "transparent",
                resize: "vertical",
                minHeight: "80px",
                boxSizing: "border-box",
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              fontSize: "12px",
              fontFamily: dt.fontMono,
              color: message.length > 1000 ? "#C0392B" : dt.textMuted,
              mt: 0.5,
            }}
          >
            {translate("partnerships:invite.messageHint", { count: message.length })}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.5,
          gap: 1,
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        {/* Email summary above buttons */}
        {email.trim() && !emailError && (
          <Box
            sx={{
              fontSize: "12px",
              color: dt.textSecondary,
              textAlign: "center",
              mb: 0.5,
              fontStyle: "italic",
            }}
          >
            {translate("partnerships:invite.emailSummary", { email: email.trim() })}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            width: "100%",
            flexDirection: isPhone ? "column-reverse" : "row",
            "& > :not(:first-of-type)": {
              ml: isPhone ? "0 !important" : undefined,
            },
          }}
        >
          <Button
            onClick={handleClose}
            disabled={actionLoading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              color: dt.textSecondary,
              borderRadius: dt.radiusSm,
              px: 2.5,
              py: 1,
              transition: "all 0.2s ease",
              width: isPhone ? "100%" : "auto",
              "&:hover": {
                backgroundColor: dt.inputBg,
                color: dt.textPrimary,
              },
            }}
          >
            {translate("common:buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={actionLoading || !email.trim()}
            variant="contained"
            startIcon={
              actionLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendOutlinedIcon sx={{ fontSize: "18px !important" }} />
              )
            }
            sx={{
              background: accentGradientLight,
              borderRadius: dt.radiusPill,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              px: 3,
              py: 1,
              boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              width: isPhone ? "100%" : "auto",
              whiteSpace: "nowrap",
              "&:hover": {
                background: accentGradientLightHover,
                transform: "translateY(-2px)",
                boxShadow: "0 6px 20px rgba(52,152,219,0.3)",
              },
              "&.Mui-disabled": {
                background: "#D1D5DB",
                color: "#9CA3AF",
                boxShadow: "none",
              },
            }}
          >
            {actionLoading
              ? translate("partnerships:invite.sending")
              : translate("partnerships:invite.send")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default InvitePartnerDialog;
